import React, { useEffect, useState } from "react";
import { Route, Routes, Navigate, useLocation } from "react-router-dom"; // Import useLocation
// Common
import Header from "./components/common/Header";
import Footer from "./components/common/Footer";

// Auth
import Login from "./components/auth/Login";
import Logout from "./components/auth/Logout";

// Public
import Home from "./components/public/Home"; // Import Home for visitors

// User Panel
import Sidebar from "./components/user/Sidebar";
import Dashboard from "./components/user/Dashboard";
import Users from "./components/user/Users";

import "./App.css";

import Products from "./components/user/Products.js";
import ProductCategory from "./components/user/ProductCategory.js";
import SubCategory from "./components/user/SubCategory.js";
import Comment from "./components/user/Comment.js";
import LikeDislike from "./components/user/LikeDislike.js";
import Feedback from "./components/user/Feedback.js";
import Gallery from "./components/user/Gallery.js";
import Blogs from "./components/user/Blogs.js";
import Restaurant from "./components/user/Restaurant.js";
import RecipeVideos from "./components/user/RecipeVideos.js";
import RestaurantCity from "./components/user/RestaurantCities.js";

import Contributions from "./components/user/Contributions.js";
import Announcement from "./components/user/Announcement.js";
import RestaurantClicks from "./components/user/RestaurantClicks.js";
import ProductClicks from "./components/user/ProductClicks.js";
import RecipeVideoClicks from "./components/user/RecipeVideoClicks.js";
import RecipeClicks from "./components/user/RecipeClicks.js";
import NearbySearchClicks from "./components/user/NearbySearchClicks.js";
import RestaurantActionClicks from "./components/user/RestaurantActionClicks.js";
import PartnerApplications from "./components/user/PartnerApplications.js";
import PartnerApplicationDetail from "./components/user/PartnerApplicationDetail.js";

import Recipes from "./components/user/Recipes.js";
import UserFavoriteRecipes from "./components/user/UserFavoriteRecipes.js";
import UserGroceryList from "./components/user/UserGroceryList.js";
import UserHealthProfiles from "./components/user/UserHealthProfiles.js";

import RecipeMasterBenefits from "./components/user/RecipeMasters/RecipeMasterBenefits.js";
import RecipeMasterDiseases from "./components/user/RecipeMasters/RecipeMasterDiseases.js";
import RecipeMasterNutrients from "./components/user/RecipeMasters/RecipeMasterNutrients.js";
import RecipeMasterTags from "./components/user/RecipeMasters/RecipeMasterTags.js";
import RecipeMasterIngredientCategories from "./components/user/RecipeMasters/RecipeMasterIngredientCategories.js";
import RecipeMasterIngredients from "./components/user/RecipeMasters/RecipeMasterIngredients.js";
import DeliveryPricingSettings from "./components/user/DeliveryPricingSettings.js";
import DeliveryGigs from "./components/user/DeliveryGigs.js";

function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const location = useLocation(); // Now works correctly inside Router context

  useEffect(() => {
    const accessTokenCheck = localStorage.getItem("accessToken");
    if (accessTokenCheck) {
      setIsLoggedIn(true);
    }
  }, []);

  // Determine if the current route is "event/:eventUrlSlug"
  const isEventDetailPage = location.pathname.startsWith("/event/");
  const isEventFormPage = location.pathname.startsWith("/event-form/");

  // console.log({isEventDetailPage});

  return (
    <div className="App wrapper">
      {/* Conditionally render Sidebar based on login and role */}

      {isLoggedIn &&
        !isEventDetailPage && // Exclude Sidebar on EventDetail page
        !isEventFormPage && <Sidebar />}

      <div
        className={
          isLoggedIn && !isEventDetailPage && !isEventFormPage
            ? "main-panel"
            : ""
        }
      >
        {/* Conditionally render Header */}
        {isLoggedIn && !isEventDetailPage && !isEventFormPage && <Header />}

        {/* Routes */}
        <Routes>
          {/* Landing page for visitors */}
          <Route
            path="/"
            element={
              isLoggedIn ? <Navigate to={"/user/dashboard"} /> : <Home />
            }
          />

          <Route
            path="/login"
            element={<Login setIsLoggedIn={setIsLoggedIn} />}
          />
          <Route path="/logout" element={<Logout />} />

          {/* User routes */}
          <Route
            path="/user/dashboard"
            element={isLoggedIn ? <Dashboard /> : <Navigate to="/" />}
          />
          <Route
            path="/user/delivery-pricing"
            element={
              isLoggedIn ? (
                <DeliveryPricingSettings />
              ) : (
                localStorage.setItem("redirectTo", location.pathname) && (
                  <Navigate to="/login" />
                )
              )
            }
          />
          <Route
            path="/user/delivery-gigs"
            element={
              isLoggedIn ? (
                <DeliveryGigs />
              ) : (
                localStorage.setItem("redirectTo", location.pathname) && (
                  <Navigate to="/login" />
                )
              )
            }
          />
          <Route
            path="/user/users"
            element={
              isLoggedIn ? (
                <Users />
              ) : (
                // Store the current route in localStorage before redirecting
                localStorage.setItem("redirectTo", location.pathname) && (
                  <Navigate to="/login" />
                )
              )
            }
          />

          {/* Add Student create route */}

          <Route
            path="/product/create"
            element={
              isLoggedIn ? (
                <Products />
              ) : (
                // Store the current route in localStorage before redirecting
                localStorage.setItem("redirectTo", location.pathname) && (
                  <Navigate to="/login" />
                )
              )
            }
          />

          {/* Add Student fees route */}
          <Route
            path="/product/category"
            element={
              isLoggedIn ? (
                <ProductCategory />
              ) : (
                // Store the current route in localStorage before redirecting
                localStorage.setItem("redirectTo", location.pathname) && (
                  <Navigate to="/login" />
                )
              )
            }
          />

          {/* Add Sub Category route */}
          <Route
            path="/sub-category"
            element={
              isLoggedIn ? (
                <SubCategory />
              ) : (
                // Store the current route in localStorage before redirecting
                localStorage.setItem("redirectTo", location.pathname) && (
                  <Navigate to="/login" />
                )
              )
            }
          />

          {/* Add Comment route */}
          <Route
            path="/user/comments"
            element={
              isLoggedIn ? (
                <Comment />
              ) : (
                // Store the current route in localStorage before redirecting
                localStorage.setItem("redirectTo", location.pathname) && (
                  <Navigate to="/login" />
                )
              )
            }
          />

          {/* Add a Gallery Route */}
          <Route
            path="/user/gallery"
            element={
              isLoggedIn ? (
                <Gallery />
              ) : (
                // Store the current route in localStorage before redirecting
                localStorage.setItem("redirectTo", location.pathname) && (
                  <Navigate to="/login" />
                )
              )
            }
          />

          {/* Add Comment route */}
          <Route
            path="/user/like/dislike"
            element={
              isLoggedIn ? (
                <LikeDislike />
              ) : (
                // Store the current route in localStorage before redirecting
                localStorage.setItem("redirectTo", location.pathname) && (
                  <Navigate to="/login" />
                )
              )
            }
          />
          {/* Add Comment route */}
          <Route
            path="/user/feedback"
            element={
              isLoggedIn ? (
                <Feedback />
              ) : (
                // Store the current route in localStorage before redirecting
                localStorage.setItem("redirectTo", location.pathname) && (
                  <Navigate to="/login" />
                )
              )
            }
          />
          {/* Add Blogs route */}
          <Route
            path="/user/blogs"
            element={
              isLoggedIn ? (
                <Blogs />
              ) : (
                // Store the current route in localStorage before redirecting
                localStorage.setItem("redirectTo", location.pathname) && (
                  <Navigate to="/login" />
                )
              )
            }
          />
          {/* Add Restaurant  route */}
          <Route
            path="/user/restaurants"
            element={
              isLoggedIn ? (
                <Restaurant />
              ) : (
                // Store the current route in localStorage before redirecting
                localStorage.setItem("redirectTo", location.pathname) && (
                  <Navigate to="/login" />
                )
              )
            }
          />

          {/* Add Restaurant Clicks route */}
          <Route
            path="/user/restaurant-clicks"
            element={
              isLoggedIn ? (
                <RestaurantClicks />
              ) : (
                // Store the current route in localStorage before redirecting
                localStorage.setItem("redirectTo", location.pathname) && (
                  <Navigate to="/login" />
                )
              )
            }
          />

          {/* Add Product Clicks route */}
          <Route
            path="/user/product-clicks"
            element={
              isLoggedIn ? (
                <ProductClicks />
              ) : (
                // Store the current route in localStorage before redirecting
                localStorage.setItem("redirectTo", location.pathname) && (
                  <Navigate to="/login" />
                )
              )
            }
          />

          {/* Add Recipe Video Clicks route */}
          <Route
            path="/user/recipe-video-clicks"
            element={
              isLoggedIn ? (
                <RecipeVideoClicks />
              ) : (
                localStorage.setItem("redirectTo", location.pathname) && (
                  <Navigate to="/login" />
                )
              )
            }
          />
          <Route
            path="/user/recipe-clicks"
            element={
              isLoggedIn ? (
                <RecipeClicks />
              ) : (
                localStorage.setItem("redirectTo", location.pathname) && (
                  <Navigate to="/login" />
                )
              )
            }
          />
          <Route
            path="/user/nearby-search-clicks"
            element={
              isLoggedIn ? (
                <NearbySearchClicks />
              ) : (
                localStorage.setItem("redirectTo", location.pathname) && (
                  <Navigate to="/login" />
                )
              )
            }
          />
          <Route
            path="/user/restaurant-actions"
            element={
              isLoggedIn ? (
                <RestaurantActionClicks />
              ) : (
                localStorage.setItem("redirectTo", location.pathname) && (
                  <Navigate to="/login" />
                )
              )
            }
          />

          {/* Add Contributions  route */}
          <Route
            path="/user/contributions"
            element={
              isLoggedIn ? (
                <Contributions />
              ) : (
                // Store the current route in localStorage before redirecting
                localStorage.setItem("redirectTo", location.pathname) && (
                  <Navigate to="/login" />
                )
              )
            }
          />

          {/* Add Announcement  route */}
          <Route
            path="/user/announcement"
            element={
              isLoggedIn ? (
                <Announcement />
              ) : (
                // Store the current route in localStorage before redirecting
                localStorage.setItem("redirectTo", location.pathname) && (
                  <Navigate to="/login" />
                )
              )
            }
          />

          {/* Add Restaurant cities  route */}
          <Route
            path="/user/restaurantCity"
            element={
              isLoggedIn ? (
                <RestaurantCity />
              ) : (
                localStorage.setItem("redirectTo", location.pathname) && (
                  <Navigate to="/login" />
                )
              )
            }
          />

          {/* Add Partner Applications Routes */}
          <Route
            path="/user/partner-applications"
            element={
              isLoggedIn ? (
                <PartnerApplications />
              ) : (
                localStorage.setItem("redirectTo", location.pathname) && (
                  <Navigate to="/login" />
                )
              )
            }
          />
          <Route
            path="/user/partner-applications/:id"
            element={
              isLoggedIn ? (
                <PartnerApplicationDetail />
              ) : (
                localStorage.setItem("redirectTo", location.pathname) && (
                  <Navigate to="/login" />
                )
              )
            }
          />

          {/* Add Recipe Management Routes */}
          <Route
            path="/user/recipe-management/recipes"
            element={
              isLoggedIn ? (
                <Recipes />
              ) : (
                localStorage.setItem("redirectTo", location.pathname) && (
                  <Navigate to="/login" />
                )
              )
            }
          />
          <Route
            path="/user/recipe-management/favorites"
            element={
              isLoggedIn ? (
                <UserFavoriteRecipes />
              ) : (
                localStorage.setItem("redirectTo", location.pathname) && (
                  <Navigate to="/login" />
                )
              )
            }
          />
          <Route
            path="/user/recipe-management/grocery"
            element={
              isLoggedIn ? (
                <UserGroceryList />
              ) : (
                localStorage.setItem("redirectTo", location.pathname) && (
                  <Navigate to="/login" />
                )
              )
            }
          />
          <Route
            path="/user/recipe-management/health"
            element={
              isLoggedIn ? (
                <UserHealthProfiles />
              ) : (
                localStorage.setItem("redirectTo", location.pathname) && (
                  <Navigate to="/login" />
                )
              )
            }
          />

          {/* Add Recipe Masters Routes */}
          <Route
            path="/user/recipe-masters/benefits"
            element={
              isLoggedIn ? (
                <RecipeMasterBenefits />
              ) : (
                localStorage.setItem("redirectTo", location.pathname) && (
                  <Navigate to="/login" />
                )
              )
            }
          />
          <Route
            path="/user/recipe-masters/diseases"
            element={
              isLoggedIn ? (
                <RecipeMasterDiseases />
              ) : (
                localStorage.setItem("redirectTo", location.pathname) && (
                  <Navigate to="/login" />
                )
              )
            }
          />
          <Route
            path="/user/recipe-masters/nutrients"
            element={
              isLoggedIn ? (
                <RecipeMasterNutrients />
              ) : (
                localStorage.setItem("redirectTo", location.pathname) && (
                  <Navigate to="/login" />
                )
              )
            }
          />
          <Route
            path="/user/recipe-masters/tags"
            element={
              isLoggedIn ? (
                <RecipeMasterTags />
              ) : (
                localStorage.setItem("redirectTo", location.pathname) && (
                  <Navigate to="/login" />
                )
              )
            }
          />
          <Route
            path="/user/recipe-masters/categories"
            element={
              isLoggedIn ? (
                <RecipeMasterIngredientCategories />
              ) : (
                localStorage.setItem("redirectTo", location.pathname) && (
                  <Navigate to="/login" />
                )
              )
            }
          />
          <Route
            path="/user/recipe-masters/ingredients"
            element={
              isLoggedIn ? (
                <RecipeMasterIngredients />
              ) : (
                localStorage.setItem("redirectTo", location.pathname) && (
                  <Navigate to="/login" />
                )
              )
            }
          />

          {/* Add Recipe videos route */}
          <Route
            path="/user/recipe/videos"
            element={
              isLoggedIn ? (
                <RecipeVideos />
              ) : (
                // Store the current route in localStorage before redirecting
                localStorage.setItem("redirectTo", location.pathname) && (
                  <Navigate to="/login" />
                )
              )
            }
          />

          {/* Catch-all route to redirect to landing page */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>

        {/* Conditionally render Footer */}
        {isLoggedIn && !isEventDetailPage && !isEventFormPage && <Footer />}
      </div>
    </div>
  );
}

export default App;
